import {
  ActionFunctionArgs,
  defer,
  json,
  LoaderFunctionArgs,
} from "@remix-run/node";
import {
  Await,
  Link,
  useFetcher,
  useLoaderData,
  useNavigate,
  useSearchParams,
} from "@remix-run/react";
import { motion } from "framer-motion";
import { CheckIcon } from "lucide-react";
import { Suspense, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import PageContent from "~/components/pages/content";
import { Button } from "~/components/ui/button";
import { CardDescription } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { ScrollArea } from "~/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Separator } from "~/components/ui/separator";
import { Skeleton } from "~/components/ui/skeleton";
import { ContentLayout } from "~/layout/content";
import { categories, projects, types } from "~/lib/constants";
import { cn } from "~/lib/utils";
import { authenticator } from "~/services/auth.server";
import { handleDraftCreate } from "~/services/drafts.server";
import { handlePostsFetch } from "~/services/posts.server";

export async function loader({ request }: LoaderFunctionArgs) {
  const user = authenticator.isAuthenticated(request);

  const posts = handlePostsFetch({
    request,
  });

  const data = {
    posts,
    user,
  };
  return defer(data);
}

export default function BlogsPage() {
  const { t } = useTranslation();
  const fetcher = useFetcher();
  const data = useLoaderData<typeof loader>();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("q") ?? "");
  const debounce = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (debounce.current) {
      clearTimeout(debounce.current);
    }

    debounce.current = setTimeout(() => {
      const currentSearch = searchParams.get("q") ?? "";

      if (search !== currentSearch) {
        setSearchParams({ q: search });
      }
    }, 500);
    return () => clearTimeout(debounce.current);
  }, [searchParams, search, setSearchParams]);

  const onCreatePost = async () => {
    const formData = new FormData();
    await fetcher.submit(formData, {
      method: "POST",
    });
  };

  return (
    <ContentLayout>
      <div className="flex flex-col items-center md:flex-row">
        <div className="flex flex-1 flex-row items-center">
          <div className="ml-4 flex max-w-sm flex-1 flex-row space-x-2">
            <Input
              value={search}
              type="search"
              placeholder="Search"
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="mt-4 md:mt-0">
          <Suspense fallback={<Skeleton className="h-8 w-32" />}>
            <Await
              resolve={data.user}
              errorElement={<p>Error loading images location!</p>}
            >
              {(user) => (
                <>
                  {user?.isAdmin && (
                    <Button
                      variant="secondary"
                      className="mr-4"
                      onClick={() => navigate("/content")}
                    >
                      {t("content_tab")}
                    </Button>
                  )}
                  {user?.isAdmin && (
                    <Button onClick={onCreatePost}>{t("new_post")}</Button>
                  )}
                </>
              )}
            </Await>
          </Suspense>
        </div>
      </div>
      <PageContent>
        <div className="flex h-full w-full flex-col-reverse md:flex-row">
          <div className="mt-8 flex w-full flex-col pr-8 md:mt-0 md:w-[85%]">
            <Suspense
              fallback={
                <>
                  {[1, 2, 3].map((_, index) => (
                    <div key={`loading-${index}`} className="w-full">
                      <div className="flex flex-col-reverse md:flex-row">
                        <div className="flex flex-1 flex-col pr-4">
                          <Skeleton className="h-8 w-full" />
                          <Skeleton className="mt-4 h-20 w-full" />
                        </div>
                        <div className="w-full self-center md:w-40">
                          <Skeleton className="h-32 w-full md:w-40" />
                        </div>
                      </div>
                      <Separator className="my-4" />
                    </div>
                  ))}
                </>
              }
            >
              <Await
                resolve={data.posts}
                errorElement={<p>Error loading posts!</p>}
              >
                {(posts) =>
                  posts.map((post, index) => (
                    <Link
                      key={post.id}
                      to={`/blogs/${post.id}`}
                      className="w-full hover:opacity-80"
                    >
                      <motion.div
                        initial={{
                          opacity: 0,
                          y: 20,
                        }}
                        animate={{
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.5,
                            delay: 0.25 * index,
                            ease: "easeOut",
                            once: true,
                          },
                        }}
                        key={post.id}
                        className="w-full hover:opacity-80"
                      >
                        <div className="flex flex-col-reverse md:flex-row">
                          <div className="flex flex-1 flex-col pr-4">
                            <Label className="mb-2 text-4xl">
                              {post.title}
                            </Label>
                            <CardDescription>
                              {post.description}
                            </CardDescription>
                          </div>
                          <div className="mb-4 w-full self-center overflow-hidden md:mb-0 md:w-40">
                            <img
                              src={post.cover}
                              alt={post.title}
                              className="h-32 w-full object-cover transition-all hover:scale-105 md:w-40"
                            />
                          </div>
                        </div>
                        <Separator className="my-4" />
                      </motion.div>
                    </Link>
                  ))
                }
              </Await>
            </Suspense>
          </div>
          <motion.div
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
              transition: {
                duration: 1,
                delay: 0.5,
                ease: "easeOut",
                once: true,
              },
            }}
            className="flex w-full flex-col md:w-[15%]"
          >
            <Label className="mb-2">{t("type")}</Label>
            <Select
              value={searchParams.get("t") || ""}
              onValueChange={(value) => {
                setSearchParams({
                  q: search,
                  c: searchParams.get("c") || "",
                  t: value,
                  p: searchParams.get("p") || "",
                });
              }}
            >
              <SelectTrigger className="mb-4">
                <SelectValue placeholder={t("all")} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value=" ">{t("all")}</SelectItem>
                  {types.map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(type)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            <Label className="mb-2">{t("project")}</Label>
            <Select
              value={searchParams.get("p") || ""}
              onValueChange={(value) => {
                setSearchParams({
                  q: search,
                  c: searchParams.get("c") || "",
                  t: searchParams.get("t") || "",
                  p: value,
                });
              }}
            >
              <SelectTrigger className="mb-4">
                <SelectValue placeholder={t("all")} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value=" ">{t("all")}</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project} value={project}>
                      {t(project)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            <Label>{t("categories")}</Label>
            <ScrollArea className="flex justify-between md:flex-col">
              {categories.map((category) => (
                <Button
                  className="justify-start text-foreground md:w-full"
                  variant="link"
                  key={category}
                  onClick={() => {
                    const selectedCategories =
                      searchParams.get("c")?.split(",") ?? [];

                    if (selectedCategories.includes(category.toLowerCase())) {
                      selectedCategories.splice(
                        selectedCategories.indexOf(category.toLowerCase()),
                        1
                      );
                    } else {
                      selectedCategories.push(category.toLowerCase());
                    }

                    setSearchParams({
                      q: search,
                      c: selectedCategories.join(","),
                      t: searchParams.get("t") || "",
                      p: searchParams.get("p") || "",
                    });
                  }}
                >
                  <Label>{t(category)}</Label>
                  <CheckIcon
                    className={cn(
                      "h-4 w-4",
                      (searchParams.get("c") ?? "").includes(
                        category.toLowerCase()
                      )
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                </Button>
              ))}
            </ScrollArea>
          </motion.div>
        </div>
      </PageContent>
    </ContentLayout>
  );
}

export async function action({ request }: ActionFunctionArgs) {
  if (request.method === "POST") {
    return handleDraftCreate({ request });
  }

  return json({ ok: true });
}
